import {
	type GetReviewsForStorefrontQueryParams,
	getReviewsForStorefront,
	pageAfterFirst,
} from '@api/reviews';
import { Logger } from '@utils/logger/logger';
import { escapeRegExp } from '@utils/regex';
import { atom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import {
	amountToRenderAtom,
	currentResultsCountAtom,
	reviewsCollectionAtom,
} from '../../shared-atoms';
import type {
	GetTextParams,
	Loadable,
	QueryParamAction,
	SSFState,
	SearchSortFilterReducerState,
} from './types';

const initialState: SSFState = {
	searchTextDisplay: '',
	searchText: undefined,
	reviews: [],
};

/** @internal */
export const queryParamInitialState: GetReviewsForStorefrontQueryParams = {
	content: undefined,
	page: 1,
	stars: undefined,
	sort: 'selectiontype:asc|createddate:desc',
};

/** @internal */
export const searchSortFilterInitialState: SearchSortFilterReducerState = {
	queryParams: queryParamInitialState,
	showText: 'hide',
	lastAction: 'ssr-hydration',
};

/** @internal */
export const getText = ({ actionType, queryParams }: GetTextParams) => {
	switch (actionType) {
		case 'search':
			return queryParams.stars && queryParams.stars.length > 0
				? 'show'
				: 'hide';
		case 'remove-star-rating':
		case 'star-rating':
			return queryParams.content ? 'show' : 'hide';
		default:
			return searchSortFilterInitialState.showText;
	}
};

export const searchSortFilterAtom = atom<SearchSortFilterReducerState>(
	searchSortFilterInitialState,
);

export const searchSortFilterReducer = (
	prev: SearchSortFilterReducerState,
	action: QueryParamAction,
): SearchSortFilterReducerState => {
	switch (action.type) {
		case 'reset':
			return {
				...searchSortFilterInitialState,
				lastAction: 'reset',
			};
		case 'fetch-more': {
			return {
				queryParams: {
					...prev.queryParams,
					page:
						prev.queryParams.page === 1
							? pageAfterFirst
							: prev.queryParams.page + 1,
				},
				showText: 'show',
				lastAction: 'fetch-more',
			};
		}
		case 'search': {
			const lastAction = action.payload ? 'search' : undefined;
			const content = action.payload ? escapeRegExp(action.payload) : undefined;
			return {
				queryParams: {
					...prev.queryParams,
					content,
					page: queryParamInitialState.page,
				},
				showText: action.payload
					? 'hide-if-loading'
					: getText({ actionType: action.type, queryParams: prev.queryParams }),
				lastAction,
			};
		}
		case 'remove-star-rating':
		case 'star-rating': {
			return {
				queryParams: {
					...prev.queryParams,
					stars: action.payload,
					page: queryParamInitialState.page,
				},
				showText:
					action.payload.length === 0
						? getText({
								actionType: action.type,
								queryParams: prev.queryParams,
							})
						: 'hide-if-loading',
				lastAction: action.type,
			};
		}
		case 'sort': {
			return {
				queryParams: {
					...prev.queryParams,
					sort: action.payload,
					page: queryParamInitialState.page,
				},
				showText:
					prev.queryParams.content || prev.queryParams.stars?.length
						? 'show'
						: 'hide',
				lastAction: 'sort',
			};
		}
		case 'toggle-photos': {
			return {
				queryParams: {
					...prev.queryParams,
					hasPhotos: action.payload,
					page: queryParamInitialState.page,
				},
				showText:
					prev.queryParams.content ||
					prev.queryParams.stars?.length ||
					action.payload
						? 'show'
						: 'hide',
				lastAction: 'toggle-photos',
			};
		}
		default:
			return prev;
	}
};

export const sortSelectionAtom = selectAtom(
	searchSortFilterAtom,
	(state) => state.queryParams.sort,
);
export const searchTextAtom = selectAtom(
	searchSortFilterAtom,
	(state) => state.queryParams.content,
);
export const isViewMoreAtom = selectAtom(
	searchSortFilterAtom,
	(state) => state.queryParams.page > 1,
);

export const searchTextDisplayAtom = atom<string>(
	initialState.searchTextDisplay,
);

export const currentStarRatingsAtom = atom<string[]>([]);
export const previousStarRatingsAtom = atom<string[]>([]);
export const areStarRatingsSavedAtom = atom<boolean>(true);

const pageAtom = selectAtom(
	searchSortFilterAtom,
	(state) => state.queryParams.page,
);
export const vendorIdAtom = atom('');

export const reviewsLoadableStatesAtom = atom<Loadable>({
	state: 'init',
});
export const shouldShowTextAtom = atom<boolean>((get) => {
	const state = get(searchSortFilterAtom);
	const loadableReview = get(reviewsLoadableStatesAtom);

	if (state.showText === 'hide-if-loading') {
		return loadableReview.state !== 'loading';
	}
	return state.showText === 'show';
});

let controller: AbortController | undefined;
export const fetchReviewsAtom = atom(null, async (get, set) => {
	controller?.abort();
	controller = new AbortController();
	const { signal } = controller;
	const page = get(pageAtom);
	const vendorId = get(vendorIdAtom);
	const { queryParams } = get(searchSortFilterAtom);
	const currentReviews = get(reviewsCollectionAtom);

	set(reviewsLoadableStatesAtom, { state: 'loading' });

	try {
		const response = await getReviewsForStorefront(
			{
				vendorId,
				...queryParams,
			},
			signal,
		);

		if (page > 1) {
			const newCollection = [...currentReviews, ...response.data];
			set(reviewsCollectionAtom, newCollection);
		} else {
			set(amountToRenderAtom, 5);
			set(reviewsCollectionAtom, response.data);
		}

		set(currentResultsCountAtom, response.totalCount);
		set(reviewsLoadableStatesAtom, { state: 'hasData' });
	} catch (error) {
		set(reviewsLoadableStatesAtom, { state: 'hasError', error: error });
		set(currentResultsCountAtom, 0);
		set(amountToRenderAtom, 0);

		Logger.error({
			log_message: 'Unable to fetch reviews for storefront',
			errorToLog: error,
			newRelic: true,
		});
	}
});

export const filterByReviewBucketAtom = atom(
	null,
	(get, set, text: '5 Star' | '4 Star' | '3 Star' | '2 Star' | '1 Star') => {
		const [rating] = text.split(' ');
		const ratingWithStar = `${rating} star${
			Number.parseInt(rating, 10) > 1 ? 's' : ''
		}`;
		set(currentStarRatingsAtom, [ratingWithStar]);
		set(previousStarRatingsAtom, [ratingWithStar]);
		set(areStarRatingsSavedAtom, true);
		set(searchSortFilterAtom, (prev) =>
			searchSortFilterReducer(prev, {
				type: 'star-rating',
				payload: [Number.parseInt(rating, 10)],
			}),
		);
		set(fetchReviewsAtom);
	},
);

export const hasPhotosAtom = selectAtom(
	searchSortFilterAtom,
	(state) => state.queryParams.hasPhotos,
);
